// Build pipeline → Units roster sub-surface.
// Create + list for mfg_units (keystone primitive). Kanban is the sibling view.
import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingUnitsTab } from "./useManufacturingUnitsTab";
import {
  ManufacturingUnitRow,
  SALE_PATH_LABELS,
  VISIBILITY_LABELS,
} from "./ManufacturingUnitRow";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function ManufacturingUnitsRoster() {
  const gate = useManufacturingEntitlementGate();
  const [navTick, setNavTick] = useState(0);
  const [newSignal, setNewSignal] = useState(0);
  const m = useManufacturingUnitsTab(navTick, newSignal);

  if (!gate.enabled) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Permanent unit UUIDs, create flow, and build timeline. Issue serial / mark complete here or
          move cards on Plant Kanban.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={() => setNewSignal((s) => s + 1)}
            disabled={gate.loading}
          >
            + New unit
          </button>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => {
              gate.refresh();
              setNavTick((s) => s + 1);
            }}
            disabled={gate.loading || m.listBusy || m.actionBusy}
          >
            {m.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      <SectionCard
        title="Create"
        description={
          "Each unit has a permanent UUID. Dealer holder and end customer are tracked separately. " +
          "Issue serial freezes materials + recipe tree; mark build complete feeds overhead production dollars."
        }
      >
        {m.creating ? (
          <div className="space-y-2">
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Plant
              <select
                className={`${inputClass} mt-1`}
                value={m.newPlantId}
                onChange={(e) => m.setNewPlantId(e.target.value)}
              >
                <option value="">(pick a plant)</option>
                {m.plants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plant_code} · {p.name}
                    {p.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Sale path
              <select
                className={`${inputClass} mt-1`}
                value={m.newSalePath}
                onChange={(e) => m.setNewSalePath(e.target.value as typeof m.newSalePath)}
              >
                {m.salePathOptions.map((sp) => (
                  <option key={sp} value={sp}>
                    {SALE_PATH_LABELS[sp] ?? sp}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Visibility
              <select
                className={`${inputClass} mt-1`}
                value={m.newVisibility}
                onChange={(e) => m.setNewVisibility(e.target.value as typeof m.newVisibility)}
              >
                {m.visibilityOptions.map((v) => (
                  <option key={v} value={v}>
                    {VISIBILITY_LABELS[v] ?? v}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Recipe (optional UUID — leave blank for ad-hoc / unknown SKU)
              <input
                className={`${inputClass} mt-1`}
                value={m.newRecipeId}
                onChange={(e) => m.setNewRecipeId(e.target.value)}
                placeholder="mfg_recipes.id"
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Inventory holder dealer org (optional UUID)
              <input
                className={`${inputClass} mt-1`}
                value={m.newInventoryHolderOrgId}
                onChange={(e) => m.setNewInventoryHolderOrgId(e.target.value)}
                placeholder="organizations.id of the dealer"
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              End-customer contact (optional UUID)
              <input
                className={`${inputClass} mt-1`}
                value={m.newEndCustomerContactId}
                onChange={(e) => m.setNewEndCustomerContactId(e.target.value)}
                placeholder="contact uuid"
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Deal (optional UUID)
              <input
                className={`${inputClass} mt-1`}
                value={m.newOpsDealId}
                onChange={(e) => m.setNewOpsDealId(e.target.value)}
                placeholder="ops_deal_entities.id"
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Notes
              <input
                className={`${inputClass} mt-1`}
                value={m.newNotes}
                onChange={(e) => m.setNewNotes(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                onClick={() => void m.submitCreate()}
                disabled={m.actionBusy}
              >
                {m.actionBusy ? "Saving…" : "Create unit"}
              </button>
              <button type="button" className={ghostBtnClass} onClick={m.cancelCreate}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-xs text-gray-500 dark:text-zinc-400">
            Use + New unit above to begin tracking a shed through its build lifecycle.
          </div>
        )}
      </SectionCard>

      <SectionCard title="Filter">
        <div className="flex flex-col gap-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Plant
            <select
              className={`${inputClass} mt-1`}
              value={m.filter.plant_id}
              onChange={(e) => m.setFilter({ ...m.filter, plant_id: e.target.value })}
            >
              <option value="">All plants</option>
              {m.plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.plant_code} · {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Sale path
            <select
              className={`${inputClass} mt-1`}
              value={m.filter.sale_path}
              onChange={(e) => m.setFilter({ ...m.filter, sale_path: e.target.value })}
            >
              <option value="">All sale paths</option>
              {m.salePathOptions.map((sp) => (
                <option key={sp} value={sp}>
                  {SALE_PATH_LABELS[sp] ?? sp}
                </option>
              ))}
            </select>
          </label>
        </div>
      </SectionCard>

      <SectionCard title="All units">
        <div {...{ [FIREFLY_TARGET_ATTR]: "mfg-units-list" }}>
          {m.listBusy ? (
            <div className="text-sm text-gray-500 dark:text-zinc-400">Loading units…</div>
          ) : null}
          {!m.listBusy && m.units.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-zinc-400">
              No units yet. Use + New unit — units are the keystone primitive that deliveries,
              sales, and repos all hang off of.
            </div>
          ) : null}
          <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
            {m.units.map((u) => (
              <ManufacturingUnitRow
                key={u.id}
                unit={u}
                plants={m.plants}
                actionBusy={m.actionBusy}
                onStartBuild={m.startBuildForUnit}
                onCompleteBuild={m.completeBuildForUnit}
              />
            ))}
          </ul>
        </div>
      </SectionCard>
    </div>
  );
}
